import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { platformLimits } from '../data/platforms';
import { chips, computeProviders, modelPlatforms, OPENROUTER_FALLBACK_COUNT } from '../data/stack';
import { COMPILED_LABEL } from '../data/sources';
import { CountResult, getOpenRouterCount } from '../utils/api';
import { PageHeader } from '../components/PageHeader';
import { DataBadge } from '../components/DataBadge';
import { AgentPlatformsTab } from '../components/platforms/AgentPlatformsTab';
import { ModelPlatformsTab } from '../components/platforms/ModelPlatformsTab';
import { ComputeTab } from '../components/platforms/ComputeTab';
import { SiliconTab } from '../components/platforms/SiliconTab';
type TabId = 'agents' | 'models' | 'compute' | 'silicon';
const TABS: Array<{
  id: TabId;
  label: string;
  noun: string;
  count: number;
}> = [{
  id: 'agents',
  label: 'Agent platforms',
  noun: 'agent platforms',
  count: platformLimits.length
}, {
  id: 'models',
  label: 'Model platforms',
  noun: 'model platforms',
  count: modelPlatforms.length
}, {
  id: 'compute',
  label: 'Compute',
  noun: 'compute providers',
  count: computeProviders.length
}, {
  id: 'silicon',
  label: 'Silicon',
  noun: 'chips tracked',
  count: chips.length
}];
export function Platforms() {
  const [params, setParams] = useSearchParams();
  const param = params.get('tab');
  const active: TabId = TABS.some(t => t.id === param) ? param as TabId : 'agents';
  const [openrouter, setOpenrouter] = useState<CountResult | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => {
    let alive = true;
    getOpenRouterCount(OPENROUTER_FALLBACK_COUNT).then(r => alive && setOpenrouter(r));
    return () => {
      alive = false;
    };
  }, []);
  const select = (id: TabId) => setParams(id === 'agents' ? {} : {
    tab: id
  }, {
    replace: true
  });
  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % TABS.length;else if (e.key === 'ArrowLeft') next = (i - 1 + TABS.length) % TABS.length;else if (e.key === 'Home') next = 0;else if (e.key === 'End') next = TABS.length - 1;else return;
    e.preventDefault();
    select(TABS[next].id);
    tabRefs.current[next]?.focus();
  };
  return <>
      <PageHeader title="Platforms" lede="The full stack beneath the machine population — from the platforms where agents are made, to where models are served, to the clouds and chips that run them." data-id="element-807" data-mp="11iwvofxee1bd">
        <DataBadge mode="snapshot" source="Curated from vendor docs, filings & announcements" asOf={COMPILED_LABEL} data-id="element-808" data-mp="1afe6tj5rhz74" />
        {openrouter && <DataBadge mode={openrouter.mode} source="OpenRouter" asOf={openrouter.asOf} data-id="element-809" data-mp="1kbr3mpwlst63" />}
      </PageHeader>

      <div className="mx-auto max-w-[1400px] px-5 md:px-8" data-id="element-810" data-mp="3r1fehelqwrf3">
        <p className="pt-6 font-mono text-[12px] text-muted" data-id="element-811" data-mp="3r41nlioabezf">
          {TABS.map((t, i) => <span key={t.id} data-id="element-812" data-mp="3m4xest59vj9m" data-mpi={i}>
              <span className="text-ink" data-id="element-813" data-mp="21l1w0wilvk37">{t.count}</span> {t.noun}
              {i < TABS.length - 1 && <span className="mx-2 text-faint" data-id="element-814" data-mp="26jwokq6dkn0i">·</span>}
            </span>)}
        </p>

        <div role="tablist" aria-label="Stack layer" className="mt-4 grid grid-cols-2 border-b border-line md:grid-cols-4" data-id="element-815" data-mp="1rv0dslcxovw7">
          {TABS.map((t, i) => {
          const selected = t.id === active;
          return <button key={t.id} ref={el => {
            tabRefs.current[i] = el;
          }} id={`tab-${t.id}`} type="button" role="tab" aria-selected={selected} aria-controls={`panel-${t.id}`} tabIndex={selected ? 0 : -1} onClick={() => select(t.id)} onKeyDown={e => onKeyDown(e, i)} className={`relative flex items-baseline justify-between gap-3 px-1 py-4 text-left transition-colors duration-150 md:px-4 ${selected ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-816" data-mp="1iu2dad1pf6id" data-mpi={i}>
                <span className="whitespace-nowrap text-[14px]" data-id="element-817" data-mp="1385whpb6cz2k">{t.label}</span>
                <span className={`font-mono text-2xl tabular ${selected ? 'text-machine' : ''}`} data-id="element-818" data-mp="13639zf98do2l">{t.count}</span>
                {selected && <span className="absolute inset-x-0 -bottom-px h-[2px] bg-machine" aria-hidden="true" data-id="element-819" data-mp="u7r7ofz051ub" />}
              </button>;
        })}
        </div>

        <div id={`panel-${active}`} role="tabpanel" aria-labelledby={`tab-${active}`} tabIndex={0} className="pt-10 focus:outline-none" data-id="element-820" data-mp="p9zhbqe09ns8">
          {active === 'agents' && <AgentPlatformsTab data-id="element-821" data-mp="fj4twip0wi57" />}
          {active === 'models' && <ModelPlatformsTab openrouter={openrouter} data-id="element-822" data-mp="bh02zrpylzm9" />}
          {active === 'compute' && <ComputeTab data-id="element-823" data-mp="bex22cn9qm2r" />}
          {active === 'silicon' && <SiliconTab data-id="element-824" data-mp="6glru0t2a9k0" />}
        </div>
      </div>
    </>;
}